import { Navigate, useLocation } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, RotateCcw } from "lucide-react";

import MagicLinkLogin from "@/components/MagicLinkLogin";
import { useAuth } from "@/contexts/AuthContext";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export default function LoginPage() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const checkoutStatus = new URLSearchParams(location.search).get("mobile_checkout");

  if (checkoutStatus === "success" || checkoutStatus === "cancel") {
    return <MobileCheckoutReturn status={checkoutStatus} />;
  }

  if (isAuthenticated && user) {
    const state = location.state as { from?: { pathname?: string } } | null;
    let redirectTo = state?.from?.pathname;

    if (!redirectTo) {
      redirectTo = user.subscription_status === "active" ? "/episodes" : "/paywall";
    }

    return <Navigate to={redirectTo} replace />;
  }

  return <MagicLinkLogin />;
}

function MobileCheckoutReturn({ status }: { status: "success" | "cancel" }) {
  const isSuccess = status === "success";
  const appURL = `podwise:///login?mobile_checkout=${status}`;

  return (
    <div className={cn("min-h-screen flex items-center justify-center px-4 py-16", theme.colors.backgroundCream)}>
      <main className={cn("w-full max-w-lg p-8 sm:p-10", theme.cards.base)}>
        <div className="flex flex-col items-center text-center">
          <div
            className={cn(
              "mb-6 flex h-20 w-20 items-center justify-center rounded-2xl",
              isSuccess ? theme.colors.warmYellow : theme.colors.lightGray,
              theme.borders.neubrutalism,
              theme.shadows.neubrutalismSm,
            )}
          >
            {isSuccess ? (
              <CheckCircle2 className="h-10 w-10 text-gray-900 dark:text-white" aria-hidden="true" />
            ) : (
              <RotateCcw className="h-10 w-10 text-gray-900 dark:text-white" aria-hidden="true" />
            )}
          </div>

          <p className="mb-3 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Podwise mobile checkout
          </p>
          <h1 className="text-3xl font-black leading-tight text-gray-900 dark:text-white sm:text-4xl">
            {isSuccess ? "Payment complete." : "Checkout canceled."}
          </h1>
          <p className="mt-4 text-base leading-7 text-gray-600 dark:text-gray-300">
            {isSuccess
              ? "Return to the Podwise app to finish unlocking your account. The app will refresh your subscription status automatically."
              : "Return to the Podwise app to choose a plan or keep using your existing account."}
          </p>

          <a
            href={appURL}
            className={cn(
              "mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-4 text-base font-black",
              theme.buttons.primary,
            )}
          >
            Open Podwise App
            <ArrowUpRight className="h-5 w-5" aria-hidden="true" />
          </a>

          <div className={cn("mt-5 rounded-xl px-4 py-3 text-sm leading-6", theme.colors.warmYellowBg, theme.colors.warmYellowBorder, "border")}>
            If the button does not open the app, switch back to Podwise manually and tap Refresh Account.
          </div>
        </div>
      </main>
    </div>
  );
}
